import { faPen, faPenToSquare } from "@fortawesome/pro-solid-svg-icons"
import { Box, Button, Grid, IconButton, Tabs, Typography, useMediaQuery, useTheme } from "@mui/material"
import { useApi } from "auth/useApi3"
import DefaultButton from "components/Button"
import CustomFieldInput from "components/CustomFieldInput"
import { Formik, useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import moment from "moment"
import {
  StyledGridBorderGrey,
  StyledTabVertical
} from "pages/customer/pages/settings/pages/users/pages/edit/UserCustomField.styled"
import React, { useEffect, useState } from "react"
import {
  StyledFontAwesomeIconEdit,
  StyledIconButtonEdit,
  StyledListItem,
  StyledSwitchDisabled
} from "shared/vehicle/CustomFields/CustomFields.styled"
import * as Yup from "yup"
import { customFieldValidation } from "./customFieldValidation"
import { t } from "i18next"

const CustomFieldValue = ({ customField, value }) => {
  switch (customField.type) {
    case "Date":
      return (
        <Typography variant="bodyMedium" fontWeight={600}>
          {value ? moment.utc(value).local().format("YYYY-MM-DD") : "-"}
        </Typography>
      )
    case "Text":
    case "Numeric":
      return (
        <Typography variant="bodyMedium" fontWeight={600}>
          {value ? value : "-"}
        </Typography>
      )
    case "Boolean":
      return value === "True" ? (
        <StyledSwitchDisabled disabled defaultChecked />
      ) : (
        <StyledSwitchDisabled disabled />
      )
    case "Notification":
    case "RepeatedNotification":
      return (
        <Box display="flex" flexDirection="column">
          {customField?.dateInfo?.date && (
            <Grid container justifyContent="flex-start">
              <Typography variant="bodyMedium" fontWeight={600}>
                {moment(customField.dateInfo.date).utc().format("YYYY-MM-DD")}
              </Typography>
              <Typography>&nbsp;{`(${customField.dateInfo.name})`}</Typography>
            </Grid>
          )}
          {customField?.numberInfo?.number && (
            <Grid container justifyContent="flex-start">
              <Typography variant="bodyMedium" fontWeight={600}>
                {customField.numberInfo.number}
              </Typography>
              <Typography>&nbsp;{`(${customField.numberInfo.name})`}</Typography>
            </Grid>
          )}
        </Box>
      )
    default:
      return <Typography>{customField.value}</Typography>
  }
}

const CategoryTabs = ({ categories, categoryTabValue, setCategoryTabValue, cachedCustomFields }) => {
  const { errors, touched } = useFormikContext()

  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"))

  const countCategoryErrors = (category) => {
    return Object.keys(errors).filter((key) => cachedCustomFields[key]?.category === category && touched[key])
      .length
  }

  return (
    <StyledGridBorderGrey>
      <Tabs
        sx={{
          "& .MuiButtonBase-root": {
            padding: "16px 30px",
            [theme.breakpoints.down("sm")]: {
              padding: "16px 20px"
            }
          },
          "& .MuiTabs-scroller": {
            marginTop: isPhone ? "0" : "40px"
          }
        }}
        indicatorColor="primary"
        value={categoryTabValue}
        onChange={(_, newValue) => {
          setCategoryTabValue(newValue)
        }}
        variant="scrollable"
        TabIndicatorProps={{ style: { left: !isPhone ? 0 : "auto" } }}
        orientation={isPhone ? "horizontal" : "vertical"}>
        {categories.map((category, index) => {
          const errorCount = countCategoryErrors(category)

          return (
            <StyledTabVertical
              id={`vertical-tab-${index}`}
              aria-controls={`vertical-tabpanel-${index}`}
              label={errorCount ? category + ` (${errorCount})` : category}
              key={index}
              tabIndex={index}
              tabValue={categoryTabValue}
            />
          )
        })}
      </Tabs>
    </StyledGridBorderGrey>
  )
}

const CustomFieldColumn = ({ vehicleData, data, editMode, setEditMode, vehicleId }) => {
  const [vehicleResult, vehicleApi] = useApi()
  const [initialFormikValues, setInitialFormikValues] = useState({})
  const [yupValidation, setYupValidation] = useState()
  const [categories, setCategories] = useState([])
  const [categoryTabValue, setCategoryTabValue] = useState(0)
  const [cachedCustomFields, setCachedCustomFields] = useState({})
  console.log("VD", vehicleData)

  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"))

  console.log("DATA", data)

  useEffect(() => {
    if (!data) return
    const cachedVehicleCustomFields = {}
    const categoriesSet = new Set()
    data.forEach((customField, parentIndex) => {
      categoriesSet.add(customField.category)
      const key = "customField" + customField.customFieldId
      cachedVehicleCustomFields[key] = {
        ...customField,
        category: customField.category,
        formikKey: key
      }
    })
    setCategories(Array.from(categoriesSet))
    setCachedCustomFields(cachedVehicleCustomFields)
  }, [data])

  useEffect(() => {
    const customFieldObject = {}
    data.forEach((customValue) => {
      customFieldObject["customField" + customValue.customFieldId] = customValue.value
    })
    setInitialFormikValues({ ...customFieldObject, customFieldValues: data })
  }, [])

  useEffect(() => {
    const shape = {}
    data.map((customField) => {
      shape["customField" + customField.customFieldId] = customFieldValidation(customField)
    })
    setYupValidation(Yup.object().shape(shape))
  }, [data])

  useEffect(() => {
    if (vehicleResult.status !== 2) return
    setTimeout(() => {
      setEditMode(!editMode)
    }, 600)
  }, [vehicleResult])

  function handleSubmit(values, submitProps) {
    const submitData = { ...vehicleData, ...values }
    if (editMode) {
      vehicleApi.put(apiEndpoints.vehicleregistryadmin.updatevehicle, submitData)
    } else {
      vehicleApi.post(apiEndpoints.vehicleregistryadmin.addvehicle, submitData)
    }
    submitProps.resetForm({ values })
  }

  return (
    <Formik
      validationSchema={yupValidation}
      onSubmit={handleSubmit}
      initialValues={initialFormikValues}
      enableReinitialize={true}>
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        dirty,
        initialValues,
        setFieldTouched
      }) => (
        <form id="CustomFields" onSubmit={handleSubmit} noValidate style={{ width: "100%" }}>
          <StyledIconButtonEdit
            top={isPhone ? 66 : 16}
            icon={faPen}
            right={24}
            editMode={editMode}
            size="medium"
            color="primary"
            onClick={() => {
              setEditMode(!editMode)
            }}>
            <StyledFontAwesomeIconEdit editMode={editMode} icon={faPen} size="lg" />
          </StyledIconButtonEdit>
          <Grid container>
            <Grid item xs={12} sm={4} lg={3} xl={2}>
              <CategoryTabs
                categories={categories}
                categoryTabValue={categoryTabValue}
                setCategoryTabValue={setCategoryTabValue}
                cachedCustomFields={cachedCustomFields}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={8}
              lg={9}
              xl={10}
              px={{ xs: 2, sm: 5 }}
              pt={5}
              pb={{ xs: 2, sm: 5 }}
              display="flex"
              flexDirection="column"
              gap="8px">
              {data &&
                data
                  .filter((customField) => customField.category === categories[categoryTabValue])
                  .map((customField, index) => {
                    return (
                      <>
                        <Typography>{customField.name}</Typography>
                        {editMode ? (
                          <CustomFieldInput
                            setFieldTouched={setFieldTouched}
                            values={values}
                            customField={customField}
                            setFieldValue={setFieldValue}
                            errors={errors}
                            touched={touched}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                            index={index}
                            initialValues={initialValues}
                          />
                        ) : (
                          <CustomFieldValue customField={customField} value={customField.value} />
                        )}
                      </>
                    )
                  })}
              {editMode && (
                <Grid container pt={3}>
                  <Grid item xs={12} display="flex" justifyContent={!isPhone && "flex-end"} gap={5}>
                    <Button
                      color="primary"
                      variant="outlined"
                      sx={{ width: "150px" }}
                      onClick={() => {
                        setEditMode(false)
                      }}>
                      {t("global.cancel", "Cancel")}
                    </Button>
                    <DefaultButton
                      color="primary"
                      variant="contained"
                      type="submit"
                      disabled={!dirty}
                      sx={{ width: "150px" }}
                      result={vehicleResult}>
                      {t("global.save", "Save")}
                    </DefaultButton>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  )
}

const CustomFields = ({ vehicleData, data, vehicleId, tabValueIndex, tabValue, setTabHistory, getCustomFields }) => {
  const [editMode, setEditMode] = useState(false)

  useEffect(() => {
      setTabHistory((prev) => {
        const alreadyVisited = prev.includes(tabValueIndex.CustomFields)
        if (!alreadyVisited && tabValue === tabValueIndex.CustomFields) {
          getCustomFields(vehicleId)
        }
      
        if (!alreadyVisited) {
          return [...prev, tabValueIndex.CustomFields]
        }
        return prev
  
      })
    }, [tabValue])


  
return (
    <Grid container direction="row" sx={{ position: "relative" }}>
      <Grid item xs={12}>

        {data? <CustomFieldColumn
          vehicleData={vehicleData}
          data={data}
          editMode={editMode}
          setEditMode={setEditMode}
          vehicleId={vehicleId}
        /> : <div>Loading..</div>}
      </Grid>
    </Grid>
  )
}

export default CustomFields
